import React, { useEffect, useState } from 'react';
import { Shield, UserCircle } from 'lucide-react';
import { useAuth } from '../AuthProvider';
import {
  getAllStaffUsers,
  updateStaffUserRole,
  updateStaffUserStatus,
  formatTimestamp,
} from '../../staffPlatformService';
import {
  DEFAULT_SUPER_ADMIN_EMAIL,
  STAFF_ROLE_LABELS,
  StaffRole,
  StaffUser,
  normalizeEmail,
} from '../../types/staffPlatform';

const UserManagementPage: React.FC = () => {
  const { canManageUsers, user, staffProfile } = useAuth();
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingEmail, setUpdatingEmail] = useState<string | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    const result = await getAllStaffUsers();
    if (result.success && result.users) {
      setUsers(result.users);
    } else {
      setError(result.error || 'Failed to load staff users');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (canManageUsers) {
      loadUsers();
    } else {
      setLoading(false);
    }
  }, [canManageUsers]);

  const getActorInfo = () => ({
    email: user?.username || staffProfile?.email || '',
    name: user?.name || staffProfile?.name || 'Staff',
  });

  const handleRoleChange = async (targetEmail: string, role: StaffRole) => {
    const { email, name } = getActorInfo();
    setUpdatingEmail(targetEmail);
    const result = await updateStaffUserRole(targetEmail, role, email, name);
    if (result.success) {
      await loadUsers();
    } else {
      alert(result.error || 'Failed to update role');
    }
    setUpdatingEmail(null);
  };

  const handleStatusToggle = async (targetEmail: string, isActive: boolean) => {
    const { email, name } = getActorInfo();
    setUpdatingEmail(targetEmail);
    const result = await updateStaffUserStatus(targetEmail, isActive, email, name);
    if (result.success) {
      await loadUsers();
    } else {
      alert(result.error || 'Failed to update status');
    }
    setUpdatingEmail(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sunbeth-orange-600" />
      </div>
    );
  }

  if (!canManageUsers) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Shield className="h-12 w-12 text-gray-400 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900">Access Denied</h2>
        <p className="text-sm text-gray-600 mt-2 max-w-md">
          Only super administrators can manage staff user accounts and roles.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">Manage staff roles and account access.</p>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Login Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((staffUser) => {
                const isDefaultSuperAdmin =
                  normalizeEmail(staffUser.email) === normalizeEmail(DEFAULT_SUPER_ADMIN_EMAIL);
                const isUpdating = updatingEmail === staffUser.email;

                return (
                  <tr key={staffUser.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <UserCircle className="h-5 w-5 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{staffUser.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {staffUser.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={staffUser.role}
                        disabled={isDefaultSuperAdmin || isUpdating}
                        onChange={(e) =>
                          handleRoleChange(staffUser.email, e.target.value as StaffRole)
                        }
                        className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-sunbeth-orange-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        {(Object.keys(STAFF_ROLE_LABELS) as StaffRole[]).map((role) => (
                          <option key={role} value={role}>
                            {STAFF_ROLE_LABELS[role]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {staffUser.loginCount ?? 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTimestamp(staffUser.lastLoginAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        type="button"
                        disabled={isDefaultSuperAdmin || isUpdating}
                        onClick={() => handleStatusToggle(staffUser.email, !staffUser.isActive)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          staffUser.isActive ? 'bg-sunbeth-orange-600' : 'bg-gray-300'
                        }`}
                        aria-label={staffUser.isActive ? 'Deactivate user' : 'Activate user'}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            staffUser.isActive ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {users.length === 0 && !error && (
          <div className="text-center py-12 text-sm text-gray-500">No staff users found.</div>
        )}
      </div>
    </div>
  );
};

export default UserManagementPage;
